import { FC, useState } from "react";
import { TFunction } from "i18next";
import { motion } from "framer-motion";
import { Card, CardContent, CardHeader } from "../../ui/card";
import { cn } from "@/lib/utils";
import { CheckIcon, ChevronUpIcon, ChevronDownIcon } from "lucide-react";
import { CircleLoader } from "../../shared/loaders";
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from "../../ui/collapsible";
import { Button } from "../../ui/button";
import { formatDuration } from "../utils";
import { ProcessingNode } from "@/constants/enums";

/**
 * Props for the ProcessingStagesCard component
 */
interface Props {
  /** Current node being processed */
  currentNode: ProcessingNode;
  /** Whether all processing stages are complete */
  processingStagesComplete?: boolean;
  /** Thinking text/reasoning content */
  thinkingText?: string;
  /** Thinking duration in milliseconds */
  thinkingDurationMs?: number;
  /** Translation function from i18next */
  t: TFunction;
}

/**
 * Displays the processing stages of the AI workflow.
 * Shows three stages: Analyzing Question, Generating Queries, and Fetching Context.
 *
 * Each stage displays:
 * - A spinner while actively processing
 * - A checkmark when complete
 * - Grayed out when not yet started
 *
 * @component
 * @example
 * <ProcessingStagesCard
 *   currentNode="QuestionGenerator"
 *   t={t}
 * />
 */
const ProcessingStagesCard: FC<Props> = ({
  currentNode,
  processingStagesComplete = false,
  thinkingText = "",
  thinkingDurationMs = 0,
  t
}) => {
  // Whether the thinking details section is expanded
  const [open, setOpen] = useState(false);

  // Show the elapsed thinking duration once all stages complete
  const showDuration = processingStagesComplete && thinkingDurationMs > 0;

  /**
   * Determines the state of each stage based on the current node and completion status
   */
  const getStageState = (stage: ProcessingNode): "completed" | "active" | "pending" => {
    // If all stages are complete or we've moved to AnswerQuestion, show all as completed
    if (processingStagesComplete || currentNode === ProcessingNode.ANSWER_QUESTION) {
      return "completed";
    }

    const stageOrder: ProcessingNode[] = [ProcessingNode.QUESTION_ANALYZER, ProcessingNode.QUESTION_GENERATOR, ProcessingNode.CONTEXT_RETRIEVER];
    const currentIndex = stageOrder.indexOf(currentNode);
    const stageIndex = stageOrder.indexOf(stage);

    // If currentNode is not in the stage order (e.g., "idle"), show all as pending
    if (currentIndex === -1) return "pending";

    // Stages before current are completed
    if (stageIndex < currentIndex) return "completed";

    // Current stage is active
    if (stageIndex === currentIndex) return "active";

    // Stages after current are pending
    return "pending";
  };

  const stages: Array<{ node: ProcessingNode; titleKey: string; descKey: string }> = [
    {
      node: ProcessingNode.QUESTION_ANALYZER,
      titleKey: "analyzingquestion",
      descKey: "analyzingquestiondesc",
    },
    {
      node: ProcessingNode.QUESTION_GENERATOR,
      titleKey: "generatingqueries",
      descKey: "generatingqueriesdesc",
    },
    {
      node: ProcessingNode.CONTEXT_RETRIEVER,
      titleKey: "fetchingcontext",
      descKey: "fetchingcontextdesc",
    },
  ];

  return (
    <motion.div initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }}>
      <Card className="border-border bg-muted/30 ml-2">
        <CardHeader className="pb-2 pt-3">
          <div className="flex items-center justify-between pb-2">
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground text-sm font-medium">
                {showDuration
                  ? t("documentchat.streamedresponse.thinkingtext", { duration: formatDuration(thinkingDurationMs) })
                  : t("documentchat.streamedresponse.thinking")}
              </span>
            </div>
            {/* Toggle to reveal thinking details */}
            {(thinkingText || showDuration) && (
              <Collapsible open={open} onOpenChange={setOpen}>
                <CollapsibleTrigger asChild>
                  <Button variant="ghost" size="icon" className="text-muted-foreground hover:text-foreground h-7 px-2">
                    {open ? (
                      <ChevronUpIcon className="size-4" />
                    ) : (
                      <ChevronDownIcon className="size-4" />
                    )}
                  </Button>
                </CollapsibleTrigger>
              </Collapsible>
            )}
          </div>
          <div className="flex flex-col gap-2">
            {stages.map((stage, index) => {
              const state = getStageState(stage.node);
              const isActive = state === "active";
              const isCompleted = state === "completed";
              const isPending = state === "pending";

              return (
                <div key={stage.node} className="relative flex items-start gap-2">
                  {/* Status icon */}
                  <div className="relative z-10 mt-0.5 shrink-0">
                    {isActive && (
                      <CircleLoader
                        visible={true}
                        height={14}
                        width={14}
                        strokeWidth={2}
                        ariaLabel="processing-loader"
                      />
                    )}
                    {isCompleted && (
                      <CheckIcon className="text-muted-foreground size-4" />
                    )}
                    {isPending && (
                      <div className="bg-muted-foreground/20 size-4 rounded-full" />
                    )}
                  </div>

                  {/* Stage content */}
                  <div className="flex-1 min-w-0">
                    <div
                      className={cn(
                        "text-sm font-medium transition-colors",
                        isActive && "text-foreground animate-pulse",
                        isCompleted && "text-muted-foreground",
                        isPending && "text-muted-foreground/50"
                      )}
                    >
                      {t(`documentchat.streamedresponse.${stage.titleKey}`)}
                    </div>
                    <div
                      className={cn(
                        "text-xs transition-colors",
                        isActive && "text-muted-foreground",
                        isCompleted && "text-muted-foreground/70",
                        isPending && "text-muted-foreground/40"
                      )}
                    >
                      {t(`documentchat.streamedresponse.${stage.descKey}`)}
                    </div>
                  </div>

                  {/* Connector line (except for last item) */}
                  {index < stages.length - 1 && (
                    <div className="absolute left-[7px] top-[20px] h-10 w-px bg-border" />
                  )}
                </div>
              );
            })}
          </div>
        </CardHeader>
        {/* Collapsible thinking details section */}
        {(thinkingText || showDuration) && (
          <Collapsible open={open}>
            <CollapsibleContent>
              <CardContent className="pt-0 pb-3">
                <p className="text-muted-foreground whitespace-pre-wrap text-xs leading-5">
                  {thinkingText || t("documentchat.streamedresponse.analyzing")}
                </p>
              </CardContent>
            </CollapsibleContent>
          </Collapsible>
        )}
      </Card>
    </motion.div>
  );
};

export default ProcessingStagesCard;
